import { useState } from "react";
import { Heart } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useToast } from "@/components/ui/use-toast";
import { useAuth } from "@/components/auth/AuthProvider";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

interface LikeButtonProps {
  productId: string;
}

const LikeButton = ({ productId }: LikeButtonProps) => {
  const { user } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  
  // Check if user has liked the product
  const { data: userLike, isLoading } = useQuery({
    queryKey: ['product-like', productId, user?.id],
    queryFn: async () => {
      if (!user?.id) return null;
      
      const { data } = await supabase
        .from('product_likes')
        .select('id')
        .eq('product_id', productId)
        .eq('user_id', user.id)
        .maybeSingle();
      
      return data;
    },
    enabled: !!user?.id
  });

  const likeMutation = useMutation({
    mutationFn: async () => {
      if (!user?.id) throw new Error('User not authenticated');
      
      const { error } = await supabase
        .from('product_likes')
        .insert({ product_id: productId, user_id: user.id });
        
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['product-like', productId] });
      toast({
        title: "¡Me gusta!",
        description: "Has indicado que te gusta este producto",
      });
    }
  });

  const unlikeMutation = useMutation({
    mutationFn: async () => {
      if (!user?.id) throw new Error('User not authenticated');
      
      const { error } = await supabase
        .from('product_likes')
        .delete()
        .eq('product_id', productId)
        .eq('user_id', user.id);
        
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['product-like', productId] });
      toast({
        title: "Ya no me gusta",
        description: "Has quitado tu 'me gusta' de este producto",
      });
    }
  });

  const handleLikeClick = () => {
    if (!user) {
      toast({
        title: "Inicia sesión",
        description: "Necesitas iniciar sesión para dar me gusta",
      });
      return;
    }

    if (userLike) {
      unlikeMutation.mutate();
    } else {
      likeMutation.mutate();
    }
  };

  return (
    <Button
      variant="ghost"
      size="sm"
      onClick={handleLikeClick}
      disabled={isLoading}
      className={`flex items-center gap-2 ${userLike ? 'text-red-500' : ''}`}
    >
      <Heart className={`w-5 h-5 ${userLike ? 'fill-current' : ''}`} />
      {userLike ? 'Me gusta' : 'Me gusta'}
    </Button>
  );
};

export default LikeButton;